// storeLocator.js
let map;

/**
 * Initialises the Google Map and the store locator application.
 * @async
 */
async function initMap() {
	const { Map } = await google.maps.importLibrary("maps");

	map = new Map(document.getElementById("map"), {
		mapId: "db939e213cca87e4",
		center: { lat: -36.252792, lng: 145.9248354 },
		zoom: 5.5,
		mapTypeControl: false,
		scaleControl: false,
		streetViewControl: false,
		rotateControl: false,
		fullscreenControl: false,
	});

	storeLocator.initApp();
}

/**
 * Store locator module.
 * @module storeLocator
 */
const storeLocator = (() => {
	const csvUrl = "https://docs.google.com/spreadsheets/d/1WYtP1YqcMXTGhIgVE9LKdMBXnFJ7GmG2q94xJzfQOlU/gviz/tq?tqx=out:csv&sheet=locations";
	const iconDefault = 'https://cdn.shopify.com/s/files/1/0747/7153/0005/files/marker-default.png?v=1747873561&width=24';
	const iconActive = 'https://cdn.shopify.com/s/files/1/0747/7153/0005/files/marker-active.png?v=1747873561';
	const iconCluster = 'https://cdn.shopify.com/s/files/1/0747/7153/0005/files/marker-cluster.png?v=1747873561';
	const iconLocation = 'https://cdn.shopify.com/s/files/1/0747/7153/0005/files/marker-active.png?v=1747873561';

	const storeList = document.querySelector('.store-locator__list');
	const searchInput = document.querySelector('.store-locator__input');
	const searchSelect = document.querySelector('.store-locator__select');
	const searchLocation = document.querySelector('.store-locator__location');
	const searchError = document.querySelector('.store-locator__error--search');
	const locationError = document.querySelector('.store-locator__error--location');
	const clearBtn = document.querySelector('.store-locator__clear');
	const resetBtn = document.querySelector('.store-locator__reset');

	let stores = [];
	let markers = [];
	let markerCluster;
	let searchMarker;

	const initialMapState = {
		center: { lat: -36.252792, lng: 145.9248354 },
		zoom: 5.5
	};

	/**
	 * Initialises the store locator application.
	 * @async
	 */
	async function initApp() {
		try {
			const csv = await fetchCSV(csvUrl);
			stores = await csvToGeoJSON(csv);

			createStoreListings(stores);
			markers = createStoreMarkers(stores, iconDefault);
			addMarkersToMap(markers);
			addClustersToMap(markers);

			setupEventListeners();
			filterStoresBySearch();

			// await findClosestStoresByUserLocation();
		} catch (error) {
			console.error("Failed to initialise the app:", error);
		}
	}

	/**
	 * Sets up event listeners for various UI elements.
	 */
	function setupEventListeners() {
		storeList.addEventListener('click', (event) => handleListingClick(event));
		map.addListener('bounds_changed', () => handleMapBoundsChange());

		resetBtn.addEventListener('click', resetApp);
		clearBtn.addEventListener('click', resetApp);
		searchInput.addEventListener('input', handleSearchInput);
		searchSelect.addEventListener('change', handleSearchSelectChange);
		searchLocation.addEventListener('click', findClosestStoresByUserLocation);
	}

	/**
	 * Fetches the CSV data from a URL.
	 * @param {string} url - The URL of the CSV file.
	 * @returns {Promise<string>} The CSV data as a string.
	 * @async
	 * @throws Will throw an error if the fetch operation fails.
	 */
	async function fetchCSV(url) {
		try {
			const response = await fetch(url);
			return response.text();
		} catch (error) {
			throw new Error("Failed to fetch CSV data");
		}
	}

	/**
	 * Converts CSV data to GeoJSON format.
	 * @param {string} csv - The CSV data as a string.
	 * @returns {Promise<Object>} The GeoJSON data.
	 */
	function csvToGeoJSON(csv) {
		return new Promise((resolve, reject) => {
			csv2geojson.csv2geojson(csv, (error, data) => {
				if (error) {
					reject(error);
				} else {
					resolve(data);
				}
			});
		});
	}

	/**
	 * Gets the user's current location and finds the closest stores.
	 * @async
	 */
	async function findClosestStoresByUserLocation() {
		const hideProgressBar = showProgressBar();

		try {
			const position = await getCurrentPosition();
			const userLocation = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);

			new google.maps.Marker({
				position: userLocation,
				map: map,
				title: 'Your Location',
				icon: {
					url: iconLocation,
					anchor: new google.maps.Point(16, 16)
				}
			});

			map.setCenter(userLocation);
			map.setZoom(10);

			const closestStores = getClosestStores(userLocation, stores.features, 25);

			createStoreListings({ type: 'FeatureCollection', features: closestStores });
			updateMarkers(closestStores);

			const cluster = markers.filter(marker => marker.getVisible());
			addClustersToMap(cluster);

			zoomToClosestStores(closestStores);
			hideErrorMessages();
		} catch (error) {
			console.error("Failed to get user's location:", error);
			showLocationError();
		} finally {
			hideProgressBar();
		}
	}

	/**
	 * Gets the current position of the user.
	 * @returns {Promise<GeolocationPosition>} The user's current position.
	 */
	function getCurrentPosition() {
		return new Promise((resolve, reject) => {
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(resolve, reject);
			} else {
				reject(new Error("Geolocation is not supported by this browser."));
			}
		});
	}

	/**
	 * Shows the progress bar and updates its width over time.
	 */
	function showProgressBar() {
		const progressBar = document.querySelector('.store-locator__progress-bar');
		progressBar.style.width = '0%';
		const loadingBar = document.querySelector('.store-locator__loading-bar');
		loadingBar.classList.remove('hide');

		let width = 0;
		const interval = setInterval(() => {
			if (width >= 90) {
				clearInterval(interval);
			} else {
				width += 10;
				progressBar.style.width = width + '%';
			}
		}, 250);

		return () => {
			clearInterval(interval);
			progressBar.style.width = '100%';
			loadingBar.classList.add('hide');
		};
	}

	/**
	 * Creates store listings and appends them to the DOM.
	 * @param {Object} stores - The GeoJSON data for the stores.
	 */
	function createStoreListings(stores) {
		const fragment = document.createDocumentFragment();

		stores.features.forEach((feature) => {
			const storeElement = document.createElement('div');
			storeElement.className = 'store-locator__item';
			storeElement.dataset.index = feature.properties.ID;

			// Format distance text if available (straight-line distance)
			const distanceText = feature.properties.distanceText || '';

			storeElement.innerHTML = `
				<div class="store-locator__item-title">${feature.properties.Name}</div>
				<p class="store-locator__item-address">${feature.properties.Address}</p>
				<p class="store-locator__item-distance">${distanceText}</p>
			`;

			fragment.appendChild(storeElement);
		});

		storeList.innerHTML = '';
		storeList.appendChild(fragment);
	}

	/**
	 * Creates store markers on the map.
	 * @param {Object} stores - The GeoJSON data for the stores.
	 * @param {string} iconUrl - The URL of the marker icon.
	 * @returns {google.maps.Marker[]} An array of markers.
	 */
	function createStoreMarkers(stores, iconUrl) {
		return stores.features.map((feature) => {
			const position = {
				lat: feature.geometry.coordinates[1],
				lng: feature.geometry.coordinates[0]
			};

			const marker = new google.maps.Marker({
				position,
				title: feature.properties.Name,
				icon: {
					url: iconUrl,
					anchor: new google.maps.Point(16, 16)
				},
				index: feature.properties.ID
			});

			marker.addListener('click', () => handleMarkerClick(marker));
			return marker;
		});
	}

	/**
	 * Adds markers to the map.
	 * @param {google.maps.Marker[]} markers - An array of markers.
	 */
	function addMarkersToMap(markers) {
		markers.forEach(marker => marker.setMap(map));
	}

	/**
	 * Adds clusters of markers to the map.
	 * @param {google.maps.Marker[]} markers - An array of markers.
	 */
	function addClustersToMap(markers) {
		if (markerCluster) {
			markerCluster.clearMarkers();
		}

		const renderer = {
			render({ count, position }) {
				return new google.maps.Marker({
					position,
					icon: {
						url: iconCluster,
						anchor: new google.maps.Point(26, 26),
					},
					label: {
						text: String(count),
						color: "rgba(255,255,255,0.9)",
						fontSize: "14px",
						fontWeight: '700'
					},
					zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
				});
			}
		};

		markerCluster = new markerClusterer.MarkerClusterer({ markers, map, renderer });
	}

	/**
	 * Flies to a specific marker on the map.
	 * @param {google.maps.Marker} marker - The marker to fly to.
	 */
	function flyToMarker(marker) {
		map.setCenter(marker.getPosition());
		map.setZoom(15);
	}

	/**
	 * Sets the active listing in the store list.
	 * @param {number} index - The index of the active listing.
	 */
	function setActiveListing(index) {
		const previouslyActive = storeList.querySelector('.store-locator__item.active');
		if (previouslyActive) {
			previouslyActive.classList.remove('active');
		}

		const newActive = storeList.querySelector(`.store-locator__item[data-index="${index}"]`);
		if (newActive) {
			newActive.classList.add('active');
			newActive.scrollIntoView({ behavior: "smooth", block: "nearest" });
		}
	}

	/**
	 * Swaps the icon of a marker.
	 * @param {google.maps.Marker} marker - The marker whose icon to swap.
	 * @param {string} iconUrl - The URL of the new icon.
	 */
	function swapMarkerIcon(marker, iconUrl) {
		marker.setIcon({
			url: iconUrl,
			anchor: new google.maps.Point(16, 16)
		});
	}

	/**
	 * Filters the store listings by the map's current bounds.
	 */
	function filterListingsByMapBounds() {
		const bounds = map.getBounds();

		markers.forEach((marker) => {
			const listing = storeList.querySelector(`.store-locator__item[data-index="${marker.index}"]`);

			if (listing) {
				if (bounds.contains(marker.getPosition())) {
					listing.style.display = '';
				} else {
					listing.style.display = 'none';
				}
			}
		});
	}

	/**
	 * Resets the store locator application to its initial state.
	 */
	function resetApp() {
		map.setCenter(initialMapState.center);
		map.setZoom(initialMapState.zoom);

		createStoreListings(stores);
		markers = createStoreMarkers(stores, iconDefault);
		addMarkersToMap(markers);
		addClustersToMap(markers);

		resetSelect();
		resetSearch();
		hideErrorMessages();

		const distanceElem = document.querySelectorAll('.store-locator__item-distance');
		distanceElem.forEach(elem => {
			elem.display = 'none';
		});
	}

	/**
	 * Resets the search input to its initial state.
	 */
	function resetSearch() {
		searchInput.value = '';
		clearBtn.classList.add('hide');
		if (searchMarker) {
			searchMarker.setVisible(false);
		}
	}

	/**
	 * Resets the select to its initial state.
	 */
	function resetSelect() {
		searchSelect.value = '';
	}

	/**
	 * Shows the reset button.
	 */
	function showResetButton() {
		resetBtn.classList.remove('hide');
	}

	/**
	 * Hides the reset button.
	 */
	function hideResetButton() {
		resetBtn.classList.add('hide');
	}

	/**
	 * Shows the location error message.
	 */
	function showLocationError() {
		storeList.classList.add('hide');
		locationError.classList.remove('hide');
		searchError.classList.add('hide');
	}

	/**
	 * Shows the search error message.
	 */
	function showSearchError() {
		storeList.classList.add('hide');
		searchError.classList.remove('hide');
		locationError.classList.add('hide');
	}

	/**
	 * Hides both error messages.
	 */
	function hideErrorMessages() {
		storeList.classList.remove('hide');
		searchError.classList.add('hide');
		locationError.classList.add('hide');
	}

	/**
	 * Filters stores based on the user's search input.
	 * @async
	 */
	async function filterStoresBySearch() {
		const options = {
			componentRestrictions: { country: "au" },
			map,
		};

		const autocomplete = new google.maps.places.Autocomplete(searchInput, options);
		map.addListener("bounds_changed", () => autocomplete.setBounds(map.getBounds()));

		searchMarker = new google.maps.Marker({ map });
		searchMarker.setVisible(false);
		let originLocation = map.getCenter();

		autocomplete.addListener("place_changed", async () => {
			searchMarker.setVisible(false);
			originLocation = map.getCenter();
			const place = autocomplete.getPlace();

			if (!place.geometry) {
				showSearchError();
				showResetButton();
				return;
			}

			originLocation = place.geometry.location;
			map.setCenter(originLocation);
			map.setZoom(map.getZoom());
			searchMarker.setPosition(originLocation);
			searchMarker.setIcon(iconLocation);
			searchMarker.setVisible(true);

			const closestStores = getClosestStores(originLocation, stores.features, 25);

			createStoreListings({ type: 'FeatureCollection', features: closestStores });
			updateMarkers(closestStores);

			const cluster = markers.filter(marker => marker.getVisible());
			addClustersToMap(cluster);

			zoomToClosestStores(closestStores);
			hideErrorMessages();
		});
	}

	/**
	 * Filters stores based on the selected state.
	 * @param {string} selectedState - The selected state.
	 * @returns {Object[]} An array of filtered stores.
	 */
	function filterStoresByState(selectedState) {
		if (selectedState === '') {
			return stores.features;
		} else {
			return stores.features.filter(feature => feature.properties.State === selectedState);
		}
	}

	/**
	 * Updates markers on the map to only show the closest stores.
	 * @param {Object[]} closestStores - An array of the closest stores.
	 */
	function updateMarkers(closestStores) {
		markers.forEach(marker => {
			const isVisible = closestStores.some(store => store.properties.ID === marker.index);
			marker.setVisible(isVisible);
		});
	}

	/**
	 * Zooms the map to fit the closest stores.
	 * @param {Object[]} closestStores - An array of the closest stores.
	 */
	function zoomToClosestStores(closestStores) {
		const bounds = new google.maps.LatLngBounds();

		closestStores.forEach(store => {
			const position = {
				lat: store.geometry.coordinates[1],
				lng: store.geometry.coordinates[0]
			};
			bounds.extend(position);
		});

		map.fitBounds(bounds);
	}

	/**
	 * Gets the closest stores to a given origin using straight-line distance.
	 * @param {Object} origin - The origin location (LatLng object).
	 * @param {Object[]} stores - An array of stores.
	 * @param {number} limit - The maximum number of stores to return.
	 * @returns {Object[]} An array of the closest stores with distance info.
	 */
	function getClosestStores(origin, stores, limit) {
		const originLat = typeof origin.lat === 'function' ? origin.lat() : origin.lat;
		const originLng = typeof origin.lng === 'function' ? origin.lng() : origin.lng;

		const storesWithDistance = stores.map(store => {
			const distance = getDistance(
				originLat,
				originLng,
				store.geometry.coordinates[1],
				store.geometry.coordinates[0]
			);

			return {
				...store,
				properties: {
					...store.properties,
					distance: distance,
					distanceText: formatDistance(distance)
				}
			};
		});

		storesWithDistance.sort((a, b) => a.properties.distance - b.properties.distance);
		return storesWithDistance.slice(0, limit);
	}

	/**
	 * Formats a distance in kilometers to a human-readable string.
	 * @param {number} distanceKm - The distance in kilometers.
	 * @returns {string} The formatted distance string.
	 */
	function formatDistance(distanceKm) {
		if (distanceKm < 1) {
			return `${Math.round(distanceKm * 1000)} m`;
		} else if (distanceKm < 10) {
			return `${distanceKm.toFixed(1)} km`;
		} else {
			return `${Math.round(distanceKm)} km`;
		}
	}

	/**
	 * Calculates the distance between two points on Earth using the Haversine formula.
	 * @param {number} lat1 - Latitude of the first point.
	 * @param {number} lon1 - Longitude of the first point.
	 * @param {number} lat2 - Latitude of the second point.
	 * @param {number} lon2 - Longitude of the second point.
	 * @returns {number} The distance between the two points in kilometers.
	 */
	function getDistance(lat1, lon1, lat2, lon2) {
		const R = 6371;
		const dLat = (lat2 - lat1) * (Math.PI / 180);
		const dLon = (lon2 - lon1) * (Math.PI / 180);
		const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) ** 2;
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	}

	/**
	 * Handles the click event on a store listing.
	 * @param {Event} event - The click event.
	 */
	function handleListingClick(event) {
		const target = event.target.closest('.store-locator__item');
		if (target) {
			const index = parseInt(target.dataset.index, 10);
			const marker = markers.find(marker => marker.index == index);

			markers.forEach(marker => swapMarkerIcon(marker, iconDefault));
			swapMarkerIcon(marker, iconActive);
			flyToMarker(marker);
			setActiveListing(marker.index);
		}
	}

	/**
	 * Handles the click event on a marker.
	 * @param {google.maps.Marker} marker - The clicked marker.
	 */
	function handleMarkerClick(marker) {
		markers.forEach(marker => swapMarkerIcon(marker, iconDefault));
		swapMarkerIcon(marker, iconActive);
		flyToMarker(marker);
		setActiveListing(marker.index);
	}

	/**
	 * Handles the map's bounds change event.
	 */
	function handleMapBoundsChange() {
		filterListingsByMapBounds();

		const currentCenter = map.getCenter();
		const currentZoom = map.getZoom();
		const isInitialCenter = currentCenter.lat() === initialMapState.center.lat && currentCenter.lng() === initialMapState.center.lng;
		const isInitialZoom = currentZoom === initialMapState.zoom;

		if (!isInitialCenter || !isInitialZoom) {
			showResetButton();
		} else {
			hideResetButton();
		}
	}

	/**
	 * Handles the input event on the search input.
	 * @param {Event} event - The input event.
	 */
	function handleSearchInput(event) {
		clearBtn.classList.toggle("hide", event.target.value.length < 1);
	}

	/**
	 * Handles the change event on the search select element.
	 * @param {Event} event - The change event.
	 */
	function handleSearchSelectChange(event) {
		const filteredByState = filterStoresByState(event.target.value);

		createStoreListings({ type: 'FeatureCollection', features: filteredByState });
		updateMarkers(filteredByState);

		const cluster = markers.filter(marker => marker.getVisible());
		addClustersToMap(cluster);

		zoomToClosestStores(filteredByState);
		showResetButton();

		resetSearch();
		hideErrorMessages();
	}

	return { initApp };
})();
